import React from "react";
import styles from "./ImportProgress.module.css";

/** Default reassurance line used by the CSV import validate/commit flows. */
export const DEFAULT_IMPORT_PROGRESS_NOTICE =
  "Large files can take a moment. Please don't close this window.";

export interface ImportProgressProps {
  /** Heading shown above the bar, e.g. "Importing patterns…". */
  heading: string;
  /** Accessible label for the progress bar, e.g. "Import in progress". */
  progressbarLabel: string;
  /** Detail line under the bar (e.g. row / pattern counts). */
  detail: React.ReactNode;
  /**
   * Reassurance line under the detail. Defaults to the standard "large files
   * take a moment / don't close this window" notice; pass a custom node to
   * override, or `null` to hide it entirely.
   */
  notice?: React.ReactNode;
}

/**
 * Presentational in-progress panel for a CSV import step (wireframe WKP-PERF).
 *
 * The import step is a single request whose real completion percentage isn't
 * known, so the bar is indeterminate rather than percentage-based. This
 * component owns no wording — callers pass the heading, detail, and (optional)
 * notice — so it stays domain-agnostic and shareable across import features
 * (work patterns, split costing validate/commit).
 */
export function ImportProgress({
  heading,
  progressbarLabel,
  detail,
  notice = DEFAULT_IMPORT_PROGRESS_NOTICE,
}: ImportProgressProps) {
  return (
    <section className={styles.progress} role="status" aria-live="polite">
      <h3 className={styles.heading}>{heading}</h3>

      <div
        className={styles.bar}
        role="progressbar"
        aria-label={progressbarLabel}
      >
        <div className={styles.barFill} />
      </div>

      <p className={styles.detail}>{detail}</p>
      {notice != null && <p className={styles.notice}>{notice}</p>}
    </section>
  );
}
